/**
 * Shared types for the application store.
 * Centralized type definitions for state management.
 */

import { MessageRole } from '@/services/chat';

import type { ImageConfigSchema } from '@features/chat/components/imageConfigSchema';
import type { StreamEvent } from '@features/chat/html/StreamRenderer';
import type { ActivityStep } from '@features/chat/utils/activitySteps';

/**
 * User information stored after authentication.
 */
export interface User {
  id: string;
  email: string;
  name?: string;
  picture?: string;
  is_admin?: boolean;
}

/**
 * LLM Provider configuration.
 */
export interface LLMProvider {
  id: string;
  name: string;
  provider: string;
  modelType?: string;
  description?: string;
  contextWindow?: number;
  maxTokens?: number;
  costPerToken?: string;
  category?: string | null;
  isActive: boolean;
  isDefault: boolean;
  sortOrder: number;
  supportsVision?: boolean;
  metadata?: Record<string, unknown>;
  configSchema?: ImageConfigSchema | null;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Artifact attached to a message (e.g., uploaded image).
 */
export interface MessageArtifact {
  id: string;
  type: 'image' | 'file' | 'other' | 'document';
  /** Thumbnail URL for display (object URL or data URL) */
  thumbUrl: string;
  /** Full-size original URL (for lightbox zoom) */
  origUrl?: string;
  /** Original filename */
  name?: string;
  /** MIME type */
  mimeType?: string;
  /** File size in bytes */
  size?: number;
}

/**
 * Chat message.
 */
/** Citation stored in message_metadata JSONB. */
interface StoredCitation {
  text: string;
  index: number;
  is_short: boolean;
}

export interface Message {
  id: string;
  role: MessageRole;
  content: string;
  isStreaming?: boolean | undefined;
  interrupted?: boolean | undefined;
  /**
   * The turn ended in an error. In-session only: a failed turn persists no
   * reply, so after a reload the user message stands alone (and offers Retry).
   */
  failed?: boolean | undefined;
  /** The server's raw error text for a failed turn, shown under Details. */
  failureDetail?: string | undefined;
  /** Attached artifacts (images, files) */
  artifacts?: MessageArtifact[] | undefined;
  /** Server-side message ID from conversation_messages table. */
  serverMessageId?: string | undefined;
  /** Subtitle text for system status messages (e.g. search query, agent intent). */
  detail?: string | undefined;
  /** Persisted citations from message_metadata (for history reload). */
  citations?: StoredCitation[] | undefined;
  /**
   * Per-turn render format for this message ('markdown' default or 'html').
   * Captured from `stream_start` (live) or history replay. Only assistant
   * turns are ever rendered as html; user messages are always escaped.
   */
  format?: 'markdown' | 'html' | undefined;
  /**
   * Transient, in-session-only stream of html tokenizer events for a live
   * html turn. Populated by `appendHtmlEvent` as open/text/close events
   * arrive. NEVER persisted (the messages slice is excluded from
   * localStorage). When present, `HtmlProse` renders live from these events;
   * otherwise it falls back to the persisted `content` string (history).
   */
  htmlEvents?: StreamEvent[] | undefined;
  /**
   * This turn's tool activity: live from tool_start/tool_complete/tool_error
   * while it streams, restored from `message_metadata.activity` on reload.
   */
  activity?: ActivityStep[] | undefined;
  /** Client clock (ms) while live; the server's stored value once reloaded. */
  turnStartedAt?: number | undefined;
  /**
   * When the message was sent (epoch ms): the client clock when created live,
   * the stored time once reloaded. Shown on hover; absent means no time shows.
   */
  sentAt?: number | undefined;
  /** When this turn ended, however it ended. Its presence means "settled". */
  turnEndedAt?: number | undefined;
  /**
   * True number of steps the turn ran. Exceeds `activity.length` only when the
   * stored record was shed to fit its ceiling, which the timeline says out loud.
   */
  activityStepCount?: number | undefined;
  /** The stored record was shed to fit its ceiling: steps, sources, or both. */
  activityTruncated?: boolean | undefined;
}

/**
 * Conversation metadata and messages.
 */
export interface Conversation {
  id: string;
  title: string;
  summary?: string;
  date?: string;
  lastMessageAt?: string;
  isActive: boolean;
  isStarred: boolean;
  messages: Message[];
  /**
   * The LLM model bound to this conversation. `null` for "Auto" (server-side
   * routing). `undefined` means the field was not present in the source data
   * (treat as "Auto" / fall back to the user's default).
   */
  modelId?: string | null;
}

// One Reminder shape: the API's. The store holds what the API returns.
export type { AgendaEvent } from '@features/agenda/services/remindersApi';

/**
 * Pagination state for list views.
 */
export interface PaginationState {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

/**
 * Default pagination values.
 */
export const defaultPagination: PaginationState = {
  page: 1,
  pageSize: 10,
  totalItems: 0,
  totalPages: 0,
  hasNext: false,
  hasPrevious: false,
};
